import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Check } from "lucide-react";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatMonthLabel(date) {
  const monthName = MONTH_NAMES[date.getMonth()];
  const twoDigitYear = String(date.getFullYear()).slice(-2);
  return `${monthName}'${twoDigitYear}`;
}

// monthsAgo = 0 -> current month, 1 -> previous month, 2 -> month before that
function getRangeForMonthsAgo(monthsAgo, monthlyCountData, claimType) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  const targetMonthDate = new Date(year, month - monthsAgo, 1);
  const targetYear = targetMonthDate.getFullYear();
  const targetMonth = targetMonthDate.getMonth();

  const startDate = new Date(targetYear, targetMonth, 1, 0, 0, 0, 0);

  let endDate;

  if (monthsAgo === 0) {
    endDate = now;
  } else {
    endDate = new Date(targetYear, targetMonth + 1, 0, 23, 59, 59, 999);
  }

  let label =
    monthsAgo === 0 ? "This Month" : formatMonthLabel(targetMonthDate);

  let count;

  if (claimType === "erroneousClaim") {
    count =
      monthsAgo === 0
        ? monthlyCountData?.thisMonth?.thisMonthErroneousCount
        : monthsAgo === 1
          ? monthlyCountData?.lastMonth?.lastMonthErroneousCount
          : monthlyCountData?.secondLastMonth?.secondLastMonthErroneousCount;
  } else if (claimType === "reprocessClaim") {
    count =
      monthsAgo === 0
        ? monthlyCountData?.thisMonth?.thisMonthReprocessCount
        : monthsAgo === 1
          ? monthlyCountData?.lastMonth?.lastMonthReprocessCount
          : monthlyCountData?.secondLastMonth?.secondLastMonthReprocessCount;
  } else if (claimType === "") {
    const normalCount =
      monthsAgo === 0
        ? monthlyCountData?.thisMonth?.thisMonthCount
        : monthsAgo === 1
          ? monthlyCountData?.lastMonth?.lastMonthCount
          : monthlyCountData?.secondLastMonth?.secondLastMonthCount;

    const erroneousCount =
      monthsAgo === 0
        ? monthlyCountData?.thisMonth?.thisMonthErroneousCount
        : monthsAgo === 1
          ? monthlyCountData?.lastMonth?.lastMonthErroneousCount
          : monthlyCountData?.secondLastMonth?.secondLastMonthErroneousCount;

    const reprocessCount =
      monthsAgo === 0
        ? monthlyCountData?.thisMonth?.thisMonthReprocessCount
        : monthsAgo === 1
          ? monthlyCountData?.lastMonth?.lastMonthReprocessCount
          : monthlyCountData?.secondLastMonth?.secondLastMonthReprocessCount;

    count = (normalCount || 0) + (erroneousCount || 0) + (reprocessCount || 0);
  } else if (claimType === "claimReceived") {
    count =
      monthsAgo === 0
        ? monthlyCountData?.thisMonth?.thisMonthClaimReceived
        : monthsAgo === 1
          ? monthlyCountData?.lastMonth?.lastMonthClaimReceived
          : monthlyCountData?.secondLastMonth?.secondLastMonthClaimReceived;
  }

  label = `${label}${"\u00A0".repeat(4)}${count ?? 0}`;

  return { startDate, endDate, label };
}

const MONTHS_AGO_LIST = [0, 1, 2];

export default function MonthRangeDropdown({
  monthlyCountData,
  claimType,
  onChange,
}) {
  // Compute the three options fresh on each mount so labels/year always
  // reflect the real current date (recalculates correctly at Jan -> Dec rollover).
  const options = useMemo(
    () =>
      MONTHS_AGO_LIST.map((monthsAgo) => ({
        monthsAgo,
        ...getRangeForMonthsAgo(monthsAgo, monthlyCountData, claimType),
      })),
    [monthlyCountData, claimType],
  );

  const [selectedMonthsAgo, setSelectedMonthsAgo] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selectedOption = options.find((o) => o.monthsAgo === selectedMonthsAgo);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") setIsOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleSelect = (monthsAgo) => {
    setSelectedMonthsAgo(monthsAgo);
    setIsOpen(false);

    const option = options.find((o) => o.monthsAgo === monthsAgo);

    if (onChange) {
      onChange({
        startDate: option.startDate,
        endDate: option.endDate,
        label: option.label,
      });
    }
  };

  return (
    <div ref={containerRef} className="relative inline-block font-sans">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`flex items-center gap-3 rounded-full border border-solid bg-neutral-50 px-5 py-3
          transition-colors duration-150
          ${isOpen ? "border-teal-600" : "border-teal-600/90 hover:border-teal-700"}
          focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2`}
      >
        <span className="text-[15px] font-lufga font-medium text-neutral-900 ">
          {selectedOption.label}
        </span>
        {/* {count !== undefined && count !== null && (
          <span className="text-[15px] font-bold text-neutral-900">
            {count}
          </span>
        )} */}
        <ChevronDown
          size={20}
          strokeWidth={2.5}
          className={`text-neutral-900 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className="absolute left-0 z-20 mt-2 w-56 list-none overflow-hidden rounded-2xl border border-neutral-200 bg-white py-2 pl-0 m-0 shadow-lg"
        >
          {options.map((option, idx) => {
            const isSelected = option.monthsAgo === selectedMonthsAgo;
            return (
              <li
                key={option.monthsAgo}
                role="option"
                aria-selected={isSelected}
              >
                <div
                  //   type="button"
                  onClick={() => handleSelect(option.monthsAgo)}
                  className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-[15px]
                    transition-colors duration-100
                    ${isSelected ? "bg-teal-50 text-teal-700 font-semibold" : "text-neutral-800 hover:bg-neutral-50"}`}
                >
                  {option.label}

                  {isSelected && (
                    <Check
                      size={16}
                      strokeWidth={3}
                      className="text-teal-600"
                    />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
